import * as locales from "date-fns/locale"

import {
  ColDef,
  ExcelStyle,
  ICellRendererParams,
  IDateFilterParams,
  IRowNode,
  IServerSideGetRowsParams,
  ValueFormatterParams,
} from "ag-grid-community"
import { Cube, Query } from "@cubejs-client/core"
import { fetchCubeResults, transformPivotQuery, transformQuery } from "../api"

import { formatFloatingPointNumber } from "utils/number-format"
import { groupBy } from "lodash"
import { removeLocaleTzOffset } from "utils/dates"
import { utcToZonedTime } from "date-fns-tz"

type ValueSymbols = "$" | "%" | undefined

export type ReportPayload = {
  query: Query
  params?: IServerSideGetRowsParams
  timeZone?: string
}

const dateFilterParams: IDateFilterParams = {
  suppressAndOrCondition: true,
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    if (cellValue == null) return -1
    var cellDate = new Date(cellValue)

    if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
      return 0
    }

    if (cellDate < filterLocalDateAtMidnight) {
      return -1
    }

    if (cellDate > filterLocalDateAtMidnight) {
      return 1
    }
    return 0
  },
  browserDatePicker: true,
  minValidYear: 2000,
  maxValidYear: 2025,
  // inRangeFloatingFilterDateFormat: "Do MMM YYYY",
}

export const numberFormatter = (
  params: ValueFormatterParams<any, any>,
  symbol?: ValueSymbols,
  valueMinDigitsFormat?: number,
) => {
  if (params.value === null) {
    return "N/A"
  }

  // We do that in order to transform values that uses exponential notation
  const fixedDecimalsValue = params.value?.toFixed(4)

  const formattedValue = formatFloatingPointNumber(
    2,
    valueMinDigitsFormat,
  )(fixedDecimalsValue)

  switch (symbol) {
    case "%":
      return formattedValue + " %"

    case "$":
      return "$ " + formattedValue

    default:
      return formattedValue
  }
}

const getCustomFieldTypeConfig = (
  timeZone: string,
  customConfig?: {
    valueSymbols: ValueSymbols
    valueMinDigitsFormat: number
    hasToRemoveFirstRow: boolean
  },
): {
  time?: ColDef
  string?: ColDef
  number?: ColDef
  boolean?: ColDef
} => {
  return {
    string: {
      filter: "agSetColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
        values: async (params: any) => {
          const colId = params.column.colId
          const query = {
            dimensions: [colId],
          }
          const records = await fetchCubeResults(
            query,
            undefined,
            customConfig?.hasToRemoveFirstRow,
          )

          if (records?.length) {
            const values = records.map((r) => Object.values(r))
            params.success(values)
          }
        },
      },
    },
    number: {
      filter: "agNumberColumnFilter",
      filterParams: {
        suppressAndOrCondition: true,
      },
      cellClass: "numberType ag-right-aligned-cell",
      type: "numericColumn",
      valueFormatter: (value) =>
        numberFormatter(
          value,
          customConfig?.valueSymbols,
          customConfig?.valueMinDigitsFormat,
        ),
    },
    time: {
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: any) => {
        const d = new Date(params.value)
        if (!(d instanceof Date) || isNaN(d.getTime())) return ""
        const zonedTime = utcToZonedTime(removeLocaleTzOffset(d), timeZone)
        const localeStr = navigator?.language?.replace("-", "")
        const locale =
          locales[localeStr as keyof typeof locales] || locales["enGB"]
        const x = new Intl.DateTimeFormat(locale.code).format(zonedTime)
        return x
      },
    },
  }
}

function prepareFieldFD(
  field: string,
  type: "dimension" | "measure",
  timeZone: string,
  c?: Cube[],
): ColDef & { meta?: Map<string, string> } {
  const fieldMeta = getFieldMeta(field, c)

  const fieldType = fieldMeta?.type || "string"
  const metaValue = fieldMeta?.meta

  const valueSymbols: ValueSymbols =
    metaValue?.["grouping_children"] === "Comparisons"
      ? "%"
      : metaValue?.["grouping_children"] === "Local" ||
          field.includes("Headcount") ||
          field.includes("base_rate")
        ? undefined
        : "$"

  const valueMinDigitsFormat =
    metaValue?.["grouping_children"] === "Comparisons" ||
    field.includes("Headcount")
      ? 0
      : 2

  const customConfig = {
    valueSymbols,
    valueMinDigitsFormat,
    hasToRemoveFirstRow: true,
  }

  const customFieldTypeConfig = getCustomFieldTypeConfig(timeZone, customConfig)

  const isDimension = type === "dimension"

  const title = metaValue?.["title"]

  const result = {
    field,
    enableRowGroup: isDimension,
    enablePivot: isDimension,
    enableValue: type === "measure",
    headerName: title ?? fieldMeta?.shortTitle,
    allowedAggFuncs: ["sum"],
    cellRenderer: (params: ICellRendererParams) => {
      // hide aggregated value in grouped Local column
      // show if grouped by currency
      const hideValue =
        metaValue?.["grouping_children"] === "Local" &&
        params.node.group &&
        !params.node.field?.includes("currency")
      const value =
        (isNaN(params.value) || field.includes("entity")
          ? params.value
          : params.valueFormatted) || ""
      return hideValue ? "" : value
    },
    meta: fieldMeta?.meta,
    ...customFieldTypeConfig[fieldType],
  }

  return result
}

function prepareField(
  field: string,
  type: "dimension" | "measure",
  timeZone: string,
  c?: Cube[],
): ColDef & { meta?: Map<string, string> } {
  const fieldMeta = getFieldMeta(field, c)
  const fieldType = fieldMeta?.type || "string"
  const customFieldTypeConfig = getCustomFieldTypeConfig(timeZone)

  const title = fieldMeta?.meta?.["title"]
  const result = {
    field,
    enableRowGroup: type === "dimension",
    enablePivot: type === "dimension",
    enableValue: type === "measure",
    headerName: title ?? fieldMeta?.shortTitle,
    allowedAggFuncs: ["sum"],
    meta: fieldMeta?.meta,
    ...customFieldTypeConfig[fieldType],
  }

  return result
}

function formatMeasures(measureFields: ColDef[]) {
  // create groups with meta key "ag_grouping". if not key, then default grouped with 'undefined'
  const groupedMeasures = groupBy(measureFields, "meta.ag_grouping")

  // separate data that needs grouping from meta keys
  const measuresWithKeysGroup = Object.keys(groupedMeasures).filter(
    (key) => key !== "undefined",
  )

  // format groups
  const groupedResult = measuresWithKeysGroup.map((key) => {
    // sub group
    // create subGroups with meta key "grouping_children""
    const subGroupedMeasures = groupBy(
      groupedMeasures[key],
      "meta.grouping_children",
    )

    // format subgroups "grouping_children"
    const subGrouping = Object.keys(subGroupedMeasures).map((x) => {
      return {
        groupId: x,
        headerName: x,
        children: subGroupedMeasures[x],
      }
    })

    return {
      groupId: key,
      headerName: key,
      children: Object.keys(subGroupedMeasures).includes("undefined")
        ? groupedMeasures[key]
        : subGrouping,
    }
  })

  // get ungrouped
  const measuresWithoutGroupKey = groupedMeasures["undefined"] || []

  const flatResult =
    Object.entries(measuresWithoutGroupKey)?.map(([key, value]) => ({
      groupId: key,
      headerName: key,
      children: value,
    })) || []

  return [...groupedResult, ...flatResult]
}

export const getColumnDefs = (
  q: Query,
  timeZone: string,
  meta?: Cube[],
  isFinancial?: boolean,
) => {
  const dimensionFields = (q.dimensions || []).map((field) =>
    isFinancial
      ? prepareFieldFD(field, "dimension", timeZone, meta)
      : prepareField(field, "dimension", timeZone, meta),
  )

  const measureFields = (q.measures || []).map((field) => ({
    ...prepareField(field, "measure", timeZone, meta),
  }))

  const measureFieldsFD = (q.measures || []).map((field) => ({
    ...prepareFieldFD(field, "measure", timeZone, meta),
  }))

  const dimensions = {
    headerName: "Dimensions",
    groupId: "dimensions",
    children: dimensionFields,
  }
  const measures = formatMeasures(isFinancial ? measureFieldsFD : measureFields)

  return [dimensions, ...measures]
}

export function getFieldMeta(field: string, metaCubes?: Cube[]) {
  const fieldsMeta = metaCubes?.flatMap((cube) => [
    ...cube.measures,
    ...cube.dimensions,
  ])

  return fieldsMeta?.find((f) => f.name === field)
}

export const excelStyles: ExcelStyle[] = [
  {
    id: "numberType",
    alignment: { horizontal: "Right" },
    numberFormat: {
      format: "#,##0.00",
    },
  },
]

export const getNodeRoute = (node: IRowNode<any>) => {
  const route = []
  let parent = node.parent
  while (parent && parent.key) {
    route.push(parent.key)
    parent = parent.parent
  }
  return route.reverse()
}

export function getExportPayload(
  payload: ReportPayload | null,
  columnState: any,
  timezone: string,
) {
  if (!payload?.params) return undefined

  const tquery = !!payload.params.request.pivotMode
    ? transformPivotQuery(payload.params, timezone)
    : transformQuery(payload.query, payload.params, timezone)

  if (!tquery) return undefined

  return {
    query: tquery,
    request: payload.params.request,
    columnState,
    timezone,
  }
}
